import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { LuPlus, LuArchive, LuArchiveRestore, LuBan, LuSearch, LuX, LuListChecks, LuCalendarDays } from 'react-icons/lu';
import api from '../../services/api';
import { Pestanas, Segmentado } from '../ui/Pantalla';
import AgendaEmpresa from './AgendaEmpresa';
import SemanaEmpresa from './SemanaEmpresa';
import { Tildable, BarraSeleccion } from '../ui/Seleccion';
import { useSeleccion, enLote } from '../../hooks/useSeleccion';
import ElegirDiaSheet from './ElegirDiaSheet';
import { diaLargo } from './diasRuta';
import { esAbierto } from '../../utils/pedidosEmpresa';
import { coincideTodo } from '../../utils/busqueda';
import { getTodayISO } from '../../utils/dateUtils';
import FilaPedido from './FilaPedido';
import ModalShell from '../ui/ModalShell';
import CancelarPedido from './CancelarPedido';

// Pedidos del Portal Empresa (11-oct-2026). Para que no canse a la vista:
// - "Hechos" muestra los últimos 60 días ("Ver anteriores" trae el resto);
// - lo hecho o cancelado se puede archivar (no se borra: pasa a "Archivados" y vuelve cuando quieran).
// Selección múltiple: cancelar varios (los que todavía no agendamos) o archivar varios.
const DIAS_VISIBLES = 60;
const fechaDe = p => new Date(p.fecha || p.actualizadoEn || p.creadoEn);
const cancelable = p => p.cancelable ?? (p.estado === 'NUEVO' && !p.ordenId);   // sin visita o «a coordinar»
const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const claveHora = p => `${p.fecha || ''} ${String(p.hora || '').padStart(5, '0')}`;

// En curso, por cuándo (13-oct-2026): lo de hoy arriba, después lo que viene, lo que espera día y lo atrasado.
function bloquesEnCurso(lista) {
    const hoy = getTodayISO();
    const b = { hoy: [], prox: [], res: [], sin: [], antes: [] };
    lista.forEach(p => {
        const f = p.fecha ? String(p.fecha).slice(0, 10) : null;
        // 28-oct-2026: una reserva de un día que ya pasó (no se llegó a agendar) no es "Reservado": va a "Sin día"
        const reservaVigente = p.rutaFecha && String(p.rutaFecha).slice(0, 10) >= hoy;
        if (!f) (reservaVigente ? b.res : b.sin).push(p); else if (f === hoy) b.hoy.push(p); else if (f > hoy) b.prox.push(p); else b.antes.push(p);
    });
    b.hoy.sort((x, y) => claveHora(x).localeCompare(claveHora(y)));
    b.prox.sort((x, y) => claveHora(x).localeCompare(claveHora(y)));
    b.antes.sort((x, y) => claveHora(y).localeCompare(claveHora(x)));
    b.res.sort((x, y) => String(x.rutaFecha).localeCompare(String(y.rutaFecha)) || (y.urgente - x.urgente));
    b.sin.sort((x, y) => (y.urgente - x.urgente) || String(y.creadoEn).localeCompare(String(x.creadoEn)));
    return [
        { id: 'hoy', titulo: 'Hoy', items: b.hoy, sinDia: true },
        { id: 'prox', titulo: 'Próximos días', items: b.prox },
        // 26-oct-2026: la empresa elige el día (pestaña Semana); el horario se confirma el día anterior
        { id: 'res', titulo: 'Reservados', nota: 'el horario se confirma el día anterior', items: b.res },
        { id: 'sin', titulo: 'Sin día', nota: 'elegí el día en Semana', items: b.sin },
        { id: 'antes', titulo: 'De días anteriores', items: b.antes },
    ].filter(x => x.items.length);
}
// Hechos, cancelados y archivados: por mes
function bloquesPorMes(lista) {
    const m = new Map();
    lista.forEach(p => {
        const d = fechaDe(p); const k = isNaN(d) ? 'sin' : `${d.getFullYear()}-${d.getMonth()}`;
        if (!m.has(k)) m.set(k, { id: k, titulo: isNaN(d) ? 'Sin fecha' : `${MESES[d.getMonth()]} ${d.getFullYear()}`, items: [] });
        m.get(k).items.push(p);
    });
    return [...m.values()];
}

export default function ListaPedidosEmpresa({ pedidos, cargando, funciones, onAbrir, onNuevo, onRecargar, puedeRuta = false }) {
    const [tab, setTab] = useState('curso');
    const [verTodo, setVerTodo] = useState(false);
    const [confirmar, setConfirmar] = useState(null);
    const [trabajando, setTrabajando] = useState(false);
    const [q, setQ] = useState('');
    const [modo, setModo] = useState('lista');   // lista | agenda (14-oct-2026) | ruta (23-oct-2026)
    const s = useSeleccion();

    const grupos = useMemo(() => {
        const vivos = pedidos.filter(p => !p.archivado);
        return {
            curso: vivos.filter(esAbierto),
            hechos: vivos.filter(p => p.estado === 'HECHO').sort((a, b) => fechaDe(b) - fechaDe(a)),
            cancelados: vivos.filter(p => p.estado === 'CANCELADO'),
            archivados: pedidos.filter(p => p.archivado),
        };
    }, [pedidos]);

    const limite = Date.now() - DIAS_VISIBLES * 86400000;
    const todosDelTab = (grupos[tab] || []).filter(p => coincideTodo(`#${p.id} ${p.lugar || ''} ${p.direccion || ''} ${p.motivo || ''} ${p.equipoSerie || ''} ${(p.equipos || []).map(e => e.serie).join(' ')}`, q));
    const viejos = tab === 'hechos' && !verTodo ? todosDelTab.filter(p => fechaDe(p) < limite).length : 0;
    const lista = viejos ? todosDelTab.filter(p => fechaDe(p) >= limite) : todosDelTab;

    // Cambiar de pestaña sale de la selección
    const { salir } = s;
    useEffect(() => { salir(); setVerTodo(false); }, [tab, salir]);

    const bloques = tab === 'curso' ? bloquesEnCurso(lista) : bloquesPorMes(lista);
    const elegidos = lista.filter(p => s.tiene(p.id));
    const aCancelar = elegidos.filter(cancelable);

    const correr = async (ids, accion, textoOk) => {
        setTrabajando(true);
        const r = await enLote(ids, accion);
        setTrabajando(false);
        setConfirmar(null);
        if (r.ok) toast.success(textoOk(r.ok));
        if (r.fallaron.length) toast.error(r.mensaje || `${r.fallaron.length} no se pudieron`);
        s.salir();
        onRecargar();
    };

    const archivar = (valor) => correr(elegidos.map(p => p.id),
        id => api.patch(`/empresa/pedidos/${id}/archivar`, { archivado: valor }),
        n => (valor ? `${n} archivado${n !== 1 ? 's' : ''}` : `${n} de vuelta en la lista`));

    // Pasar al día… (23-oct-2026): los que todavía no tienen día confirmado van a la ruta de ese día
    const aMover = elegidos.filter(p => p.estado === 'NUEVO' && !p.ordenId);
    const pasarAlDia = async (fecha) => {
        setConfirmar(null); setTrabajando(true);
        try {
            await api.post('/empresa/semana/reservar', { pedidoIds: aMover.map(p => p.id), fecha });
            toast.success(fecha ? `${aMover.length} en la ruta del ${diaLargo(fecha)}` : `${aMover.length} sin día`);
            s.salir(); onRecargar();
        } catch (e) { toast.error(e?.response?.data?.mensaje || 'No se pudo guardar'); }
        finally { setTrabajando(false); }
    };

    const acciones = tab === 'curso'
        ? [...(puedeRuta ? [{ label: 'Pasar al día', Icon: LuCalendarDays, principal: true, cantidad: aMover.length, disabled: !aMover.length,
            onClick: () => setConfirmar('dia') }] : []),
           { label: 'Cancelar', Icon: LuBan, peligro: true, cantidad: aCancelar.length, disabled: !aCancelar.length,
            onClick: () => setConfirmar('cancelar') }]
        : tab === 'archivados'
            ? [{ label: 'Sacar de archivados', Icon: LuArchiveRestore, principal: true, onClick: () => archivar(false) }]
            : [{ label: 'Archivar', Icon: LuArchive, principal: true, onClick: () => archivar(true) }];

    return (
        <>
            <div className="flex items-center justify-between gap-2">
                <Segmentado valor={modo} onChange={setModo} opciones={[{ id: 'lista', label: 'Lista' }, { id: 'agenda', label: 'Agenda' },
                    { id: 'ruta', label: 'Semana' }]} />
                <button type="button" onClick={onNuevo}
                    className="h-10 px-3.5 shrink-0 rounded-xl inline-flex items-center gap-1.5 bg-[#C9341F] text-white text-label font-black whitespace-nowrap active:scale-95"><LuPlus size={16} /> Nuevo pedido</button>
            </div>

            {modo === 'ruta' ? <SemanaEmpresa onCambio={onRecargar} /> : modo === 'agenda' ? <AgendaEmpresa pedidos={pedidos} onAbrirPedido={onAbrir} onCreado={onRecargar} /> : <>
            <label className="flex items-center gap-2 h-11 px-3 rounded-xl bg-card border border-black/[0.06] dark:border-white/[0.06]">
                <LuSearch size={16} className="text-muted shrink-0" />
                <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar lugar, N/S o #"
                    className="flex-1 min-w-0 bg-transparent text-body text-ink outline-none placeholder:text-muted" />
                {q && <button type="button" onClick={() => setQ('')} aria-label="Borrar"><LuX size={15} className="text-muted" /></button>}
            </label>

            <Pestanas activo={tab} onChange={setTab} items={[
                { id: 'curso', label: 'En curso', count: grupos.curso.length },
                { id: 'hechos', label: 'Hechos', count: grupos.hechos.length },
                ...(grupos.cancelados.length ? [{ id: 'cancelados', label: 'Cancelados', count: grupos.cancelados.length }] : []),
                ...(grupos.archivados.length ? [{ id: 'archivados', label: 'Archivados', count: grupos.archivados.length }] : []),
            ]} />

            {cargando ? (
                <p className="py-16 text-center text-muted text-body">Cargando…</p>
            ) : lista.length === 0 && !viejos ? (
                <div className="py-16 text-center space-y-2">
                    <p className="text-body-lg font-black text-ink">{q ? 'Ninguno coincide' : tab === 'curso' ? 'No hay pedidos en curso' : 'Nada por acá'}</p>
                    {tab === 'curso' && !q && <p className="text-body text-muted">Cuando un equipo necesite servicio, tocá “Nuevo pedido”.</p>}
                </div>
            ) : (
                <div className={`space-y-4 ${s.activo ? 'pb-36' : ''}`}>
                    {tab === 'hechos' && viejos > 0 && lista.length === 0 && <p className="py-6 text-center text-body text-muted">Nada en los últimos {DIAS_VISIBLES} días.</p>}
                    {bloques.map((b, i) => (
                        <section key={b.id} className="space-y-1.5">
                            <div className="flex items-center gap-2 px-1 min-h-[28px]">
                                <p className="flex-1 min-w-0 text-label font-black uppercase tracking-widest text-muted truncate">
                                    {b.titulo} <span className="opacity-70">· {b.items.length}</span>
                                    {b.nota && <span className="normal-case tracking-normal font-bold"> · {b.nota}</span>}
                                </p>
                                {i === 0 && (
                                    <button type="button" onClick={() => (s.activo ? s.salir() : s.entrar())} aria-pressed={s.activo}
                                        className={`shrink-0 h-7 px-2.5 rounded-lg inline-flex items-center gap-1 text-label font-black ${s.activo ? 'bg-ink text-page' : 'text-secondary'}`}>
                                        <LuListChecks size={14} /> {s.activo ? 'Listo' : 'Seleccionar'}
                                    </button>
                                )}
                            </div>
                            <div className={s.activo ? 'space-y-1.5' : 'rounded-2xl bg-card border border-black/[0.06] dark:border-white/[0.06] divide-y divide-black/[0.06] dark:divide-white/[0.06] overflow-hidden'}>
                                {b.items.map(p => (
                                    <Tildable key={p.id} activo={s.activo} tildado={s.tiene(p.id)} onAlternar={() => s.alternar(p.id)}>
                                        <div className={s.activo ? 'rounded-2xl bg-card overflow-hidden' : ''}>
                                            <FilaPedido p={p} funciones={funciones} mostrarDia={!b.sinDia} soloFecha={tab !== 'curso'} onClick={() => onAbrir(p)} />
                                        </div>
                                    </Tildable>
                                ))}
                            </div>
                        </section>
                    ))}
                    {viejos > 0 && (
                        <button type="button" onClick={() => setVerTodo(true)}
                            className="w-full h-11 rounded-xl bg-chip text-label font-black text-secondary">Ver anteriores ({viejos})</button>
                    )}
                    {tab === 'archivados' && <p className="pt-1 text-center text-caption text-muted">Archivar solo los saca de la lista: no se borra nada.</p>}
                </div>
            )}

            </>}

            {s.activo && modo === 'lista' && (
                <BarraSeleccion cantidad={s.cantidad} total={lista.length} onTodos={() => s.todos(lista.map(p => p.id))}
                    onSalir={s.salir} acciones={trabajando ? [] : acciones} />
            )}

            {confirmar === 'dia' && (
                <ElegirDiaSheet titulo={`Pasar ${aMover.length} pedido${aMover.length !== 1 ? 's' : ''} al día…`}
                    subtitulo={elegidos.length > aMover.length ? `${elegidos.length - aMover.length} ya tienen el día confirmado: esos no se mueven` : 'Entran en tu ruta de ese día'}
                    actual={aMover.length && aMover.every(p => p.rutaFecha === aMover[0].rutaFecha) ? aMover[0].rutaFecha : null}
                    conSinDia={aMover.some(p => p.rutaFecha)}
                    onCerrar={() => setConfirmar(null)} onElegir={pasarAlDia} />
            )}

            {confirmar === 'cancelar' && (
                <ModalShell titulo={`Cancelar ${aCancelar.length} pedido${aCancelar.length !== 1 ? 's' : ''}`}
                    subtitulo={elegidos.length > aCancelar.length
                        ? `${elegidos.length - aCancelar.length} ya tienen el día confirmado: esos se cancelan escribiendo en el pedido` : null}
                    onCerrar={() => setConfirmar(null)}>
                    <CancelarPedido modo="empresa" onVolver={() => setConfirmar(null)}
                        onConfirmar={(motivo) => correr(aCancelar.map(p => p.id), id => api.patch(`/empresa/pedidos/${id}/cancelar`, { motivo }),
                            n => `${n} pedido${n !== 1 ? 's' : ''} cancelado${n !== 1 ? 's' : ''}`)} />
                </ModalShell>
            )}
        </>
    );
}
